import type { ApiConfig, AppEnvironment } from '@blufin/types';
import { AppMode, LOCAL_BUILD_VERSION_PREFIX, VALID_ENVIRONMENTS } from '@blufin/types';
import { AbstractEnvironmentClientAlpha } from './abstract/AbstractEnvironmentClientAlpha';

export type ViteMetaEnv = Record<string, string | boolean>;

/**
 * Base runtime configuration loaded from config.json.
 * All fields are mandatory.
 */
export interface BaseRuntimeConfig<TCustom extends object = {}> {
  /** HTTP client configuration for service endpoints */
  apiConfig: ApiConfig;
  /** Custom app configuration fields (excludes base fields) */
  appConfig?: TCustom;
  /** Deployment environment from config.json */
  environment: AppEnvironment;
}

/**
 * Complete runtime configuration combining base + custom fields.
 * Generic type parameter allows apps to extend with custom configuration.
 */
export type RuntimeConfig<TCustom extends object = {}> = TCustom;

/**
 * React-specific environment client interface.
 * Extends base interface with React-specific 'mode' property.
 */
export interface IReactEnvironmentClientAlpha<TCustom extends object = {}> {
  /** HTTP client configuration from config.json */
  readonly apiConfig: ApiConfig;
  /** Custom app configuration fields (excludes base fields) */
  readonly appConfig: RuntimeConfig<TCustom>;
  /** Deployment environment from config.json */
  readonly environment: AppEnvironment;
  /** Get all environment variables as a single object */
  getAllEnvironmentVariables(): Omit<IReactEnvironmentClientAlpha<TCustom>, 'getAllEnvironmentVariables'>;
  /** Dev = hmr, production build = compiled. Note: "compiled" does not necessarily mean the app is running in prod! */
  readonly mode: AppMode;
  /** App version from build (calendar format: YYYY.WW.PATCH) */
  readonly version: string;
}

/**
 * React environment client for loading configuration from Vite environment and config.json.
 * Reads build-time variables from Vite's import.meta.env and runtime config from config.json.
 */
export class ReactEnvironmentClientAlpha<TCustom extends object = {}>
  extends AbstractEnvironmentClientAlpha<RuntimeConfig<TCustom>>
  implements IReactEnvironmentClientAlpha<TCustom>
{
  public readonly mode: AppMode;

  /**
   * Creates an instance of ReactEnvironmentClientAlpha.
   * @param viteMetaEnv - The Vite meta environment object containing environment variables.
   * @param runtimeConfig - The runtime configuration loaded from config.json (base + custom fields nested under config).
   */
  constructor(viteMetaEnv: ViteMetaEnv, runtimeConfig: BaseRuntimeConfig<TCustom> & { config?: TCustom }) {
    /** Compute configuration values */
    const version = (viteMetaEnv.VITE_APP_VERSION as string) || LOCAL_BUILD_VERSION_PREFIX;
    const { environment, apiConfig, appConfig = {} as TCustom } = runtimeConfig;
    /** Initialize base class with computed config */
    super({ apiConfig, appConfig, environment, version });
    /** Perform validation after base init (validation methods now accessible on this) */
    this._validateViteEnv(viteMetaEnv);
    this._validateRuntimeConfig(runtimeConfig);
    /** Determine mode from Vite's built-in variables */
    this.mode = viteMetaEnv.DEV ? AppMode.HMR : AppMode.COMPILED;
  }

  /**
   * Gets all environment variables as a single object.
   */
  public getAllEnvironmentVariables(): Omit<IReactEnvironmentClientAlpha<TCustom>, 'getAllEnvironmentVariables'> {
    return {
      apiConfig: this.apiConfig,
      appConfig: this.appConfig,
      environment: this.environment,
      mode: this.mode,
      version: this.version
    };
  }

  /**
   * Validates that the Vite environment object contains all required variables.
   * @param env - The environment object to validate.
   */
  private _validateViteEnv(env: ViteMetaEnv): void {
    const required = ['DEV', 'PROD', 'MODE'];
    const missing = required.filter(k => !Object.prototype.hasOwnProperty.call(env, k));
    if (missing.length) throw new Error(`Missing required Vite environment variables: ${missing.join(', ')}`);
    if (typeof env.DEV !== 'boolean') throw new Error('DEV must be a boolean');
    if (typeof env.PROD !== 'boolean') throw new Error('PROD must be a boolean');
  }

  /**
   * Validates that the runtime configuration contains all required base fields.
   * @param config - The combined runtime config (base + custom fields nested under config).
   */
  private _validateRuntimeConfig(config: BaseRuntimeConfig & { config?: TCustom }): void {
    this.validateRequiredField(config.environment, 'environment');
    this.validateRequiredField(config.apiConfig, 'apiConfig');
    this.validateEnvironmentValue(config.environment, VALID_ENVIRONMENTS as string[]);
  }
}

/** For backward compatibility, IEnvironmentClientAlpha is now the React-specific interface */
export type { IReactEnvironmentClientAlpha as IEnvironmentClientAlpha };
